"use client";

import blogApi from "@/api/blog";
import { ProjectCard, ProjectCardProps } from "@/features/Home/components";
import RecommenderCard from "@/features/Home/components/RecommenderCard";
import React, { useEffect, useState, useRef, useCallback } from "react";
import ScrollToTopButton from "../Home/components/ScrollToTopButton";
import Image from "next/image";

// Helper function to extract YouTube video ID and thumbnail
const getYouTubeInfo = (url: string) => {
  const regex =
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/;
  const match = url.match(regex);
  const videoId = match ? match[1] : null;

  if (videoId) {
    return {
      videoId,
      thumbnail: `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`,
      lowResThumbnail: `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`,
    };
  }

  return null;
};

type TVideo = {
  url: string;
  title: string;
};
const BLOG_LIMIT = 12;

const videos: TVideo[] = [
  {
    url: "https://youtu.be/Mstq-GMEaFg?si=OhzF3vB1NWUzO2TE",
    title: "คุยชีวิต คิดตามเฮีย | โอกาส ความคิด จังหวะชีวิต สู่ความสำเร็จ",
  },
  {
    url: "https://youtu.be/PqckWBl0je8?si=lrVfgM2AFPDuy6Vz",
    title: "คลิปบรรยากาศพิธีมอบรางวัล Srikrung Eastern Awards 2025",
  },
  {
    url: "https://www.youtube.com/watch?v=iCsW5Ae2Um8",
    title: "คอร์สติวสอบใบอนุญาตนายหน้าประกันวินาศภัยศรีกรุงโบรคเกอร์",
  },
  {
    url: "https://www.youtube.com/watch?v=cX1kjgIzQpU",
    title: "วิธีเช็กผลสอบและผลอบรมในระบบ E-Licensing ต่อบัตรนายหน้าทางออนไลน์",
  },
  {
    url: "https://www.youtube.com/watch?v=z09EyewoVWA",
    title:
      "Line Official Account ศรีกรุงโบรคเกอร์ พร้อมให้บริการตลอด 7 วัน 24 ชั่วโมง",
  },
  {
    url: "https://www.youtube.com/watch?v=xUIrbbhpsUU",
    title:
      "ศรีกรุงโบรคเกอร์ | อบรมออนไลน์ | หัวข้อ : ธุรกิจประเภทเครือข่าย MGM",
  },
  {
    url: "https://www.youtube.com/watch?v=R3xdTvwL-zc",
    title: " พ.ร.บ. ให้คุณอนันต์ หากไม่ทำให้โทษ มหันต์ 3",
  },
  {
    url: "https://www.youtube.com/watch?v=nCj-UEl5OYY",
    title: "การประกันภัยรถยนต์ภาคสมัครใจ“รถบรรทุก”",
  },
  {
    url: "https://youtu.be/Mstq-GMEaFg?si=OhzF3vB1NWUzO2TE",
    title:
      "ธุรกิจที่ดี ไม่ใช่แค่สร้างรายได้ แต่ต้องสร้าง ความมั่นคง ให้เราและคนที่เรารัก ด้วยระบบ MGM",
  },
];

const BlogsPage = () => {
  const [blogs, setBlogs] = useState<ProjectCardProps[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    getBlogs(BLOG_LIMIT);
  }, []);

  // Debounced scroll handler for better performance
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const handleScroll = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        const scrollThreshold = 1000;
        const isNearBottom =
          window.innerHeight + window.scrollY >=
          document.body.offsetHeight - scrollThreshold;

        if (isNearBottom && blogs.length < total && !isLoading) {
          getBlogs(Math.min(blogs.length + BLOG_LIMIT, total), true);
        }
      }, 100); // 100ms debounce
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      clearTimeout(timeoutId);
    };
  }, [blogs.length, total, isLoading]);

  const getBlogs = async (limit?: number, append = false) => {
    try {
      setIsLoading(true);
      const data = await blogApi.list({ limit });

      if (append) {
        setBlogs((prev) => {
          // Remove duplicates
          const existingIds = new Set(
            prev.map((blog: ProjectCardProps) => blog.id),
          );
          const newBlogs = data.blogs.filter(
            (blog: ProjectCardProps) => !existingIds.has(blog.id),
          );
          return [...prev, ...newBlogs];
        });
      } else {
        setBlogs(data.blogs);
      }
      setTotal(data.total);
    } catch (error) {
      console.error("Error fetching blogs:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const getAllBlogs = async () => {
    await getBlogs(total, true);
  };

  return (
    <div className="px-4 md:px-[10%]  pt-6 md:pt-[100px]">
      <ScrollToTopButton />

      {/* บทความ */}
      <section className="bg-white w-full">
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800">
            บทความ
          </h1>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 md:gap-10">
          {isLoading && blogs.length === 0
            ? // Initial loading skeleton
              Array.from({ length: 6 }).map((_, idx) => (
                <div key={idx} className="animate-pulse">
                  <div className="bg-gray-200 h-48 rounded-lg mb-4"></div>
                  <div className="h-4 bg-gray-200 rounded mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                </div>
              ))
            : blogs.map((props, idx) => (
                <div
                  key={props.id || idx}
                  className="transform transition-all duration-300 hover:scale-105"
                >
                  <ProjectCard {...props} />
                </div>
              ))}
        </div>

        {total > BLOG_LIMIT && (
          <div className="flex justify-center mt-10">
            <button
              className="bg-green-600 hover:bg-green-700 text-white text-base font-semibold w-full max-w-xs h-12 rounded-md transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              onClick={getAllBlogs}
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="flex items-center justify-center">
                  <svg
                    className="animate-spin h-4 w-4 mr-2"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  กำลังโหลด...
                </span>
              ) : (
                "เพิ่มเติม"
              )}
            </button>
          </div>
        )}
      </section>

      {/* คอร์สออนไลน์ */}
      <section className="bg-white w-full mt-16 md:mt-20 mb-16">
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800">
            คอร์สออนไลน์
          </h1>
        </div>

        <div className="flex flex-col gap-10">
          {videos.map(({ url, title }, index) => (
            <VideoPlayer key={index} url={url} title={title} index={index} />
          ))}
        </div>
      </section>
      <RecommenderCard />
    </div>
  );
};

// Video Player Component with Thumbnail First
const VideoPlayer = ({
  url,
  title,
  index,
}: {
  url: string;
  title: string;
  index: number;
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [shouldShowIframe, setShouldShowIframe] = useState(false);
  const [imageError, setImageError] = useState(false);
  const videoRef = useRef<HTMLDivElement>(null);

  const videoInfo = getYouTubeInfo(url);

  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      if (entry.isIntersecting) {
        setIsVisible(true);
      }
    },
    [],
  );

  useEffect(() => {
    const observer = new IntersectionObserver(handleIntersection, {
      threshold: 0.1,
      rootMargin: "100px",
    });

    if (videoRef.current) {
      observer.observe(videoRef.current);
    }

    return () => {
      if (videoRef.current) {
        observer.unobserve(videoRef.current);
      }
    };
  }, [handleIntersection]);

  const handleImageError = () => {
    setImageError(true);
  };

  const handleVideoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setShouldShowIframe(true);
  };

  return (
    <div
      ref={videoRef}
      className={`flex flex-col md:flex-row gap-3 md:gap-[100px] items-center justify-center opacity-0 animate-fade-in ${
        isVisible ? "opacity-100" : "opacity-0"
      }`}
      style={{
        animationDelay: `${index * 100}ms`,
        animationFillMode: "forwards",
      }}
    >
      <div className="w-full h-[280px] md:w-[640px] md:h-[360px] rounded-lg overflow-hidden shadow-lg relative group">
        <iframe
          src={`https://www.youtube.com/embed/${videoInfo?.videoId}?rel=0&showinfo=0&modestbranding=1&autohide=1`}
          title={title}
          width="100%"
          height="100%"
          className="w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          loading="lazy"
        />
      </div>
      <p className="text-lg md:text-xl text-gray-700 text-center max-w-[340px]">
        {title}
      </p>
    </div>
  );
};

export default BlogsPage;
